"use client";

import { IBaseLabelValue } from "@/interfaces";
import { useRouter } from "next/navigation";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "../../ui";
import { cn } from "@/lib";
import InputDataContent from "../inputDataContent";
import { routes } from "@/routes";
import { useTranslations } from "next-intl";
import DownloadPdf from "../../pdfTemplates/downloadPdf";
import { IRawPurchasedMilitaryServiceInquiryResData } from "@/interfaces/inquiries/governmental";
import PdfTextTemplate from "@/components/pdfTemplates/pdfTextTemplate";
import { useRef } from "react";

const MilitaryServiceInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedMilitaryServiceInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const { description, qualified } = data.data.data;
  const { national_code, birth_date } = data.data.input;
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("militaryServiceStatus.inputLabel"),
      value: national_code,
    },
    {
      label: t("militaryServiceStatus.birth_date"),
      value: birth_date,
    },
  ];
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const resultContent = !qualified ? (
    <p className="text-justify text-sm md:text-base">
      بر اساس بررسی انجام‌شده در پایگاه اطلاعات نظام وظیفه عمومی، فرد مورد نظر
      با کد ملی {national_code}
      <span className="mx-1 font-semibold" style={{ color: "#c10007" }}>
        &nbsp; مشمول خدمت وظیفه عمومی بوده
      </span>{" "}
      و ملزم به انجام خدمت سربازی طبق قوانین و مقررات جاری کشور می‌باشد.
    </p>
  ) : (
    <p className="text-justify text-sm md:text-base">
      بر اساس استعلام انجام‌شده از سامانه نظام وظیفه عمومی، فرد مورد نظر با کد
      ملی {national_code}
      <span className="mx-1 font-semibold" style={{ color: "#00a63e" }}>
        در حال حاضر مشمول خدمت وظیفه عمومی نمی‌باشد
      </span>
      این وضعیت می‌تواند ناشی از انجام خدمت، معافیت دائم یا موقت مطابق ضوابط
      قانونی و یا عدم داشتن حداقل سن مجاز برای خدمت سربازی باشد.
    </p>
  );
  return (
    <div className="shadow-main from-teal-ocean relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          {t("militaryServiceStatus.title")}
        </h2>

        <InputDataContent content={inputContent} />
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("militaryServiceStatus.resultTitle")}
          </h2>
          <DownloadPdf
            htmlContentRef={pdfContentRef}
            useHtmlToPdf={true}
            id="military-service-inquiry-download-pdf"
            fileName="وضعیت-خدمت-سربازی"
            tableTitle="وضعیت خدمت سربازی"
          />
        </FlexBetween>
        <div className="shadow-main mb-10 flex flex-col gap-6 rounded-xl p-4 py-8 sm:p-8 md:gap-8">
          {!qualified ? (
            <p className="text-justify text-sm md:text-base">
              بر اساس بررسی انجام‌شده در پایگاه اطلاعات نظام وظیفه عمومی، فرد
              مورد نظر{" "}
              <span className="mx-1 text-red-700">
                مشمول خدمت وظیفه عمومی بوده
              </span>{" "}
              و ملزم به انجام خدمت سربازی طبق قوانین و مقررات جاری کشور می‌باشد.
            </p>
          ) : (
            <p className="text-justify text-sm md:text-base">
              بر اساس استعلام انجام‌شده از سامانه نظام وظیفه عمومی،{" "}
              <span className="text-success-main mx-1">
                فرد مورد نظر در حال حاضر مشمول خدمت وظیفه عمومی نمی‌باشد
              </span>
              . این وضعیت می‌تواند ناشی از انجام خدمت، معافیت دائم یا موقت،
              مطابق ضوابط قانونی و یا عدم داشتن حداقل سن مجاز برای خدمت سربازی
              باشد.
            </p>
          )}
        </div>
        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="military-service-inquiry-download-pdf"
            htmlContentRef={pdfContentRef}
            fileName="وضعیت-خدمت-سربازی"
            tableTitle="وضعیت خدمت سربازی"
            useHtmlToPdf={true}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-military-service-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-military-service-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Military_Service_Status.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTextTemplate
          title={`نتیجه استعلام وضعیت نظام وظیفه `}
          resultContent={resultContent}
          code={data.code}
          dateTime={data.created_at}
        />
      </div>
    </div>
  );
};

export default MilitaryServiceInquiryDetails;
